/**
 * Operator Dashboard — "week runway" concept (design card 4a).
 * A 7-day strip is the spine; tonight gets a countdown hero with the
 * run-of-show; below, only what's broken (Needs attention) and how each
 * venue is trending. Theme-aware via Radix tokens.
 *
 * Everything is derived client-side from the existing events/quizzes/venues
 * fetches, plus one full fetch of tonight's event (for run-of-show pills and
 * preflight). No new endpoints.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import GettingStarted from './GettingStarted';
import {
  quizContentCounts,
  type PreflightRound,
} from '../../utils/preflight';
import './Dashboard.css';

interface LiveEvent {
  id: string;
  pin: string;
  date: string;
  status: string;
  venue: { name: string } | null;
  quiz: { id: string; name: string } | null;
  _count?: { teams: number };
}

interface Quiz {
  id: string;
  name: string;
  updatedAt: string;
  _count?: { rounds: number };
}

interface FullRound extends PreflightRound {
  orderIndex: number;
  title: string;
}

interface FullEvent {
  id: string;
  pin: string;
  date: string;
  venue: { name: string } | null;
  name: string | null;
  quiz: {
    id: string;
    name: string;
    showDrawingSlide: boolean;
    drawingAfterRound: number | null;
    rounds: FullRound[];
  };
  welcome?: { prizes: string | null; rulesText: string | null; startTime: string | null } | null;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const Dashboard: React.FC = () => {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [venueCount, setVenueCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tonightFull, setTonightFull] = useState<FullEvent | null>(null);
  const [nowMinute, setNowMinute] = useState(() => new Date());

  // Re-render each minute so the "doors in" countdown stays live.
  useEffect(() => {
    const t = setInterval(() => setNowMinute(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get('/api/events').catch(() => ({ data: { data: { events: [] } } })),
      api.get('/api/quizzes').catch(() => ({ data: { data: { quizzes: [] } } })),
      api.get('/venues').catch(() => ({ data: [] })),
    ]).then(([eventsRes, quizzesRes, venuesRes]) => {
      if (!mounted) return;
      const evs: LiveEvent[] = eventsRes.data?.data?.events ?? [];
      setEvents(evs);
      setQuizzes(quizzesRes.data?.data?.quizzes ?? []);
      setVenueCount((venuesRes.data ?? []).length);
      setLoading(false);

      // Full fetch of tonight's first event for the hero.
      const now = new Date();
      const tonight = evs.find((e) => e.date && isSameDay(new Date(e.date), now));
      if (tonight) {
        api.get(`/api/events/${tonight.id}`)
          .then((r) => { if (mounted) setTonightFull(r.data.data.event); })
          .catch(() => {});
      }
    });
    return () => { mounted = false; };
  }, []);

  const now = nowMinute;

  // ── 7-day runway: this week (Sun→Sat containing today) ──
  const week = useMemo(() => {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay()); // back to Sunday
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      const ev = events.find((e) => e.date && isSameDay(new Date(e.date), day));
      return { day, ev, isToday: isSameDay(day, now) };
    });
  }, [events, now]);

  // ── Tonight hero ──
  const tonightEvent = events.find((e) => e.date && isSameDay(new Date(e.date), now)) ?? null;

  // ── Needs attention (cheap, honest derivations) ──
  const attention = useMemo(() => {
    const items: { icon: 'warn' | 'note'; text: React.ReactNode; action: string; to: string }[] = [];
    // Tonight's preflight gaps (we have full content).
    if (tonightFull) {
      const c = quizContentCounts(tonightFull.quiz.rounds);
      if (c.clipsMissing > 0) {
        items.push({
          icon: 'warn',
          text: <><b>{tonightFull.quiz.name}</b> — {c.clipsMissing} audio clip{c.clipsMissing > 1 ? 's' : ''} still missing</>,
          action: 'Fix', to: `/quizzes/${tonightFull.quiz.id}/edit`,
        });
      }
      // No "visual images missing" nag: visual rounds run off the
      // slideshow, never an uploaded imagePath, so this fired on every
      // show night and its Fix button led to an uploader that no longer
      // exists.
      if (c.answersMissing > 0) {
        items.push({
          icon: 'warn',
          text: <><b>{tonightFull.quiz.name}</b> — {c.answersMissing} question{c.answersMissing > 1 ? 's' : ''} without an answer</>,
          action: 'Fix', to: `/quizzes/${tonightFull.quiz.id}/edit`,
        });
      }
      // Phones mark every answer wrong on a multiple choice question with
      // no correct choice.
      if (c.choiceKeysMissing > 0) {
        items.push({
          icon: 'warn',
          text: <><b>{tonightFull.quiz.name}</b> — {c.choiceKeysMissing} multiple choice question{c.choiceKeysMissing > 1 ? 's' : ''} without a correct choice</>,
          action: 'Fix', to: `/quizzes/${tonightFull.quiz.id}/edit`,
        });
      }
    }
    // Empty / imported-shell quizzes (0 rounds).
    for (const q of quizzes) {
      if ((q._count?.rounds ?? 0) === 0) {
        items.push({
          icon: 'note',
          text: <><b>{q.name}</b> imported with 0 rounds</>,
          action: 'Review', to: `/quizzes/${q.id}/edit`,
        });
      }
    }
    return items.slice(0, 6);
  }, [tonightFull, quizzes]);

  // ── Venue pulse: last 6 completed/past nights per venue ──
  const venuePulse = useMemo(() => {
    const byVenue = new Map<string, { date: Date; teams: number }[]>();
    for (const e of events) {
      if (!e.date || !e.venue?.name) continue;
      const d = new Date(e.date);
      if (d > now) continue; // only nights that have happened
      const arr = byVenue.get(e.venue.name) ?? [];
      arr.push({ date: d, teams: e._count?.teams ?? 0 });
      byVenue.set(e.venue.name, arr);
    }
    return Array.from(byVenue.entries())
      .map(([venue, nights]) => {
        const last6 = nights.sort((a, b) => a.date.getTime() - b.date.getTime()).slice(-6);
        const latest = last6[last6.length - 1]?.teams ?? 0;
        const prev = last6[last6.length - 2]?.teams ?? latest;
        const max = Math.max(1, ...last6.map((n) => n.teams));
        return { venue, last6, latest, trend: latest - prev, max };
      })
      .filter((v) => v.last6.length > 0)
      .sort((a, b) => b.latest - a.latest)
      .slice(0, 4);
  }, [events, now]);

  const greeting = (() => {
    const h = now.getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  })();

  return (
    <div className="dash">
      {/* Header */}
      <div className="dash-head">
        <div className="dash-greet-block">
          <span className="dash-greet">{greeting}</span>
          <span className="dash-date">
            {now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          </span>
        </div>
        <Link to="/live-events" className="dash-btn ghost">+ Game night</Link>
        <Link to="/quizzes" className="dash-btn primary">+ Quiz</Link>
      </div>

      {!loading && (
        <GettingStarted
          venueCount={venueCount}
          quizCount={quizzes.length}
          eventCount={events.length}
          hasCompletedEvent={events.some((e) => e.status === 'COMPLETE')}
        />
      )}

      {/* 7-day runway */}
      <div className="dash-runway">
        {week.map(({ day, ev, isToday }) => (
          <div key={day.toISOString()} className={`dash-day ${isToday ? 'today' : ev ? 'has' : 'empty'}`}>
            <span className="dash-day-dow">{DOW[day.getDay()]}{isToday ? ' · TODAY' : ''}</span>
            {ev ? (
              <>
                <span className="dash-day-venue">{ev.venue?.name ?? 'Venue'}</span>
                <span className="dash-day-meta">{ev.quiz?.name ?? 'No quiz'}</span>
              </>
            ) : (
              <Link to="/live-events" className="dash-day-add">+ add</Link>
            )}
          </div>
        ))}
      </div>

      {/* Tonight hero */}
      {tonightEvent && (
        <TonightHero event={tonightEvent} full={tonightFull} now={now} />
      )}
      {!loading && !tonightEvent && (
        <div className="dash-empty-hero">
          <span>No game night tonight.</span>
          <div className="dash-empty-actions">
            <Link to="/quizzes" className="dash-btn ghost">Write a quiz</Link>
            <Link to="/live-events" className="dash-btn ghost">Schedule a night</Link>
          </div>
        </div>
      )}

      {/* Needs attention + Venue pulse */}
      <div className="dash-grid">
        <div>
          <div className="dash-overline">NEEDS ATTENTION{attention.length ? ` · ${attention.length}` : ''}</div>
          {attention.length === 0 ? (
            <div className="dash-clear">Nothing needs attention — you&rsquo;re set. 🎉</div>
          ) : (
            <div className="dash-attn-list">
              {attention.map((a, i) => (
                <div key={i} className="dash-attn">
                  <span className={`dash-attn-icon ${a.icon}`}>{a.icon === 'warn' ? '⚠' : '✎'}</span>
                  <span className="dash-attn-text">{a.text}</span>
                  <Link to={a.to} className="dash-attn-action">{a.action} →</Link>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="dash-overline">VENUE PULSE · TEAMS PER NIGHT, LAST 6</div>
          {venuePulse.length === 0 ? (
            <div className="dash-clear">No completed nights yet.</div>
          ) : (
            <div className="dash-pulse">
              {venuePulse.map((v) => (
                <div key={v.venue} className="dash-pulse-row">
                  <span className="dash-pulse-venue">{v.venue}</span>
                  <span className="dash-pulse-bars">
                    {v.last6.map((n, i) => (
                      <span
                        key={i}
                        className={`dash-bar ${i === v.last6.length - 1 ? 'latest' : i >= v.last6.length - 3 ? 'mid' : 'early'}`}
                        style={{ height: `${Math.max(12, Math.round((n.teams / v.max) * 100))}%` }}
                        title={`${n.teams} teams · ${n.date.toLocaleDateString()}`}
                      />
                    ))}
                  </span>
                  <span className="dash-pulse-count">
                    {v.latest}{' '}
                    {v.trend > 0 ? <span className="up">▲</span> : v.trend < 0 ? <span className="down">▼</span> : null}
                  </span>
                </div>
              ))}
              <Link to="/analytics" className="dash-pulse-link">full analytics →</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Tonight hero card — countdown, run-of-show pills, preflight, actions.
function TonightHero({ event, full, now }: { event: LiveEvent; full: FullEvent | null; now: Date }) {
  const start = new Date(event.date);
  const diffMs = start.getTime() - now.getTime();
  const diffMin = Math.round(diffMs / 60000);
  const countdown = (() => {
    if (diffMin <= 0 || diffMin > 12 * 60) return null;
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return h > 0 ? `DOORS IN ${h}H ${m}M` : `DOORS IN ${m}M`;
  })();

  const rounds = full?.quiz.rounds ?? [];
  const counts = rounds.length ? quizContentCounts(rounds) : null;
  // imagesMissing is deliberately not part of this: visual rounds carry no
  // uploaded image, so counting them would keep preflight permanently red.
  const preflightClear = counts
    ? counts.clipsMissing === 0 && counts.answersMissing === 0 && counts.choiceKeysMissing === 0
    : null;

  const prizes = full?.welcome?.prizes ? full.welcome.prizes.split('\n').map((l) => l.trim()).filter(Boolean).join(' / ') : null;

  return (
    <div className="dash-hero">
      <div className="dash-hero-body">
        <div className="dash-hero-overline">
          TONIGHT{countdown ? ` · ${countdown}` : ''}
        </div>
        <div className="dash-hero-title">
          {event.quiz?.name ?? 'Game night'}{' '}
          <span className="dash-hero-venue">at {event.venue?.name ?? 'venue'}</span>
        </div>
        {rounds.length > 0 && (
          <div className="dash-pills">
            {rounds.map((r) => (
              <span
                key={r.orderIndex}
                className={`dash-pill ${r.roundType === 'AUDIO' ? 'audio' : r.roundType === 'VISUAL' ? 'visual' : ''}`}
              >
                {r.roundType === 'AUDIO' ? '♪ ' : r.roundType === 'VISUAL' ? '🖼 ' : ''}
                R{r.orderIndex + 1} {r.title || ''}
              </span>
            ))}
            {full?.quiz.showDrawingSlide && (
              <span className="dash-pill drawing">
                🎲 drawing{full.quiz.drawingAfterRound ? ` after R${full.quiz.drawingAfterRound}` : ''}
              </span>
            )}
          </div>
        )}
        <div className="dash-hero-meta">
          {preflightClear != null && (
            preflightClear
              ? <>Preflight <b className="ok">✓ all clear</b> · </>
              : <>Preflight <b className="warn">needs attention</b> · </>
          )}
          PIN <b className="pin">{event.pin}</b>
          {prizes && <> · prizes: {prizes}</>}
        </div>
      </div>
      <div className="dash-hero-actions">
        <Link to={`/events/${event.id}/host`} className="dash-btn primary big">Open host panel</Link>
        <div className="dash-hero-secondary">
          <Link to={`/events/${event.id}/score`} className="dash-btn ghost">Score</Link>
          <a href={`/present/${event.pin}`} target="_blank" rel="noopener noreferrer" className="dash-btn ghost">Projector</a>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
